
import { User, Lead, Company, CallStat, UserRole, LeadComment, Appointment, AppointmentType, SubscriptionPlan, SystemConfig, PlanConfig, CurrencyConfig, LeadActivity, LeadActivityType, Notification, NotificationType, Opportunity, OpportunityItem, Task, LeadSheetTemplate, LoginResult, AssignmentRule, ChatConversation, ChatMessage, ChatSearchResult } from '../types';
import { MOCK_COMPANIES, MOCK_USERS, MOCK_LEADS, MOCK_CALL_STATS } from '../constants';
import { isDbMode, isDemoMode } from './runtimeMode';

const API_BASE = 'api/index.php';

/**
 * Réponse paginée standard pour les endpoints qui supportent ?page=&limit=.
 * Le backend renvoie ce shape uniquement quand la pagination est demandée
 * (rétrocompat : sans `?page=`/`?limit=`, l'endpoint renvoie toujours un array complet).
 */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}
const STORAGE_KEY_LEADS = 'orion_crm_leads_v2';
const STORAGE_KEY_COMPANIES = 'orion_crm_companies_v2';
const STORAGE_KEY_USERS = 'orion_crm_users_v2';
const STORAGE_KEY_CONFIG = 'orion_crm_config_v2';
const STORAGE_KEY_APPTS = 'orion_crm_appts_v2';
const STORAGE_KEY_ACTIVITIES = 'orion_crm_activities_v2';
const STORAGE_KEY_NOTIFICATIONS = 'orion_crm_notifications_v2';
const STORAGE_KEY_OPPORTUNITIES = 'orion_crm_opportunities_v1';
const STORAGE_KEY_TASKS = 'orion_crm_tasks_v1';
// Access token en sessionStorage (tab-isolé, effacé à la fermeture)
// Refresh token géré côté serveur via httpOnly cookie
const SESSION_KEY_ACCESS_TOKEN = 'orion_access_token';
export const AUTH_EXPIRED_EVENT = 'orion:auth-expired';
const ALLOW_LOCAL_AUTH_FALLBACK = isDemoMode && Boolean((import.meta as any).env?.DEV);

const DEFAULT_PLANS: PlanConfig[] = [
  { id: 'BASIC', label: 'Starter', priceMonthly: 29, priceYearly: 290, userLimit: 3, features: ['3 utilisateurs', 'Prospects illimités', 'Support email'] },
  { id: 'PRO', label: 'Growth', priceMonthly: 99, priceYearly: 990, userLimit: 15, features: ['15 utilisateurs', 'Prospects illimités', 'Support prioritaire', 'Analytics avancés'] },
  { id: 'ENTERPRISE', label: 'Scale', priceMonthly: 299, priceYearly: 2990, userLimit: 100, features: ['100 utilisateurs', 'Prospects illimités', 'Support 24/7', 'API complète', 'Personnalisation'] }
];

const DEFAULT_CURRENCIES: CurrencyConfig[] = [
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'GBP', name: 'Pound Sterling', symbol: '£' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' }
];

const DEFAULT_ACTIVITY_SECTORS: string[] = [
  'Informatique / SaaS',
  'Marketing & Communication',
  'BTP / Construction',
  'Sante',
  'Industrie',
  'Finance / Assurance',
  'Immobilier',
  'Commerce / Distribution',
  'Logistique / Transport',
  'Education / Formation'
];

// Amélioration de la gestion du localStorage avec alertes utilisateur
const loadFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    if (Array.isArray(fallback) && !Array.isArray(parsed)) return fallback;
    return parsed;
  } catch (e) {
    console.error(`Erreur de chargement ${key}, utilisation des données par défaut.`, e);
    return fallback;
  }
};

let sessionLeads: Lead[] = isDemoMode ? loadFromStorage(STORAGE_KEY_LEADS, [...MOCK_LEADS]) : [];
let sessionCompanies: Company[] = isDemoMode ? loadFromStorage(STORAGE_KEY_COMPANIES, [...MOCK_COMPANIES]) : [];
let sessionUsers: (User & { password?: string })[] = isDemoMode ? loadFromStorage(STORAGE_KEY_USERS, [...MOCK_USERS]) : [];
let sessionConfig: SystemConfig = loadFromStorage(STORAGE_KEY_CONFIG, {
  plans: DEFAULT_PLANS,
  currencies: DEFAULT_CURRENCIES,
  activitySectors: DEFAULT_ACTIVITY_SECTORS
});
let sessionAppointments: Appointment[] = loadFromStorage(STORAGE_KEY_APPTS, []);
let sessionActivities: LeadActivity[] = loadFromStorage(STORAGE_KEY_ACTIVITIES, []);
let sessionNotifications: Notification[] = loadFromStorage(STORAGE_KEY_NOTIFICATIONS, []);
let sessionOpportunities: Opportunity[] = loadFromStorage(STORAGE_KEY_OPPORTUNITIES, []);
let sessionTasks: Task[] = loadFromStorage(STORAGE_KEY_TASKS, []);
let localComments: LeadComment[] = [];
let authExpiryNotified = false;

const notifyAuthExpired = () => {
  if (authExpiryNotified) return;
  authExpiryNotified = true;
  sessionStorage.removeItem(SESSION_KEY_ACCESS_TOKEN);
  localStorage.removeItem('auth_token'); // nettoyage rétrocompat
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
  setTimeout(() => {
    authExpiryNotified = false;
  }, 1000);
};

// Compteur d'erreurs de stockage pour alerter l'utilisateur
let storageErrorCount = 0;

const saveToStorage = () => {
  try {
    if (isDemoMode) {
      localStorage.setItem(STORAGE_KEY_LEADS, JSON.stringify(sessionLeads));
      localStorage.setItem(STORAGE_KEY_COMPANIES, JSON.stringify(sessionCompanies));
      localStorage.setItem(STORAGE_KEY_USERS, JSON.stringify(sessionUsers));
    }
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(sessionConfig));
    localStorage.setItem(STORAGE_KEY_APPTS, JSON.stringify(sessionAppointments));
    localStorage.setItem(STORAGE_KEY_ACTIVITIES, JSON.stringify(sessionActivities));
    localStorage.setItem(STORAGE_KEY_NOTIFICATIONS, JSON.stringify(sessionNotifications));
    localStorage.setItem(STORAGE_KEY_OPPORTUNITIES, JSON.stringify(sessionOpportunities));
    localStorage.setItem(STORAGE_KEY_TASKS, JSON.stringify(sessionTasks));
    storageErrorCount = 0;
  } catch (e) {
    console.error("Erreur de sauvegarde localStorage", e);
    storageErrorCount++;
    
    if (storageErrorCount >= 3) {
      alert("⚠️ ATTENTION: Espace de stockage saturé!\n\nVos modifications récentes n'ont pas pu être sauvegardées.\nVeuillez libérer de l'espace ou exporter vos données.");
      storageErrorCount = 0;
    }
  }
};

// Fonction pour créer une activité dans la timeline
const createActivity = (
  leadId: string,
  userId: string,
  userName: string,
  type: LeadActivityType,
  description: string,
  metadata?: any
): LeadActivity => {
  const activity: LeadActivity = {
    id: 'activity_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
    leadId,
    userId,
    userName,
    type,
    description,
    metadata,
    createdAt: new Date().toISOString()
  };
  
  sessionActivities.push(activity);
  saveToStorage();

  if (isDbMode) {
    safeFetch(`${API_BASE}?action=add_lead_activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: activity.id,
        leadId: activity.leadId,
        userId: activity.userId,
        userName: activity.userName,
        type: activity.type,
        description: activity.description,
        metadata: activity.metadata || null
      })
    }).catch(() => {});
  }
  
  return activity;
};

// Timeout augmenté à 3 secondes
const refreshAuthToken = async (): Promise<boolean> => {
  try {
    // Le refresh token est dans le httpOnly cookie, envoyé automatiquement via credentials: 'include'
    const res = await fetch(`${API_BASE}?action=refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({})
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (!data?.token) return false;
    sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, data.token);
    return true;
  } catch {
    return false;
  }
};

const safeFetch = async (url: string, options?: RequestInit & { timeout?: number }, retried = false) => {
  try {
    if (typeof AbortController === 'undefined') return null;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), options?.timeout || 10000);
    const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN);
    const headers: HeadersInit = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {})
    };

    try {
      const res = await fetch(url, { ...options, headers, credentials: 'include', signal: controller.signal });
      clearTimeout(id);
      const rotatedToken = res.headers.get('X-Access-Token');
      if (rotatedToken) {
        sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, rotatedToken);
      }
      const isAuthEndpoint = url.includes('action=login') || url.includes('action=refresh');
      if (res.status === 401) {
        if (!isAuthEndpoint && !retried) {
          const refreshed = await refreshAuthToken();
          if (refreshed) {
            return safeFetch(url, options, true);
          }
        }
        if (!isAuthEndpoint && token) {
          notifyAuthExpired();
        }
        // Pour les endpoints auth (login), retourner le corps d'erreur tel quel
        if (isAuthEndpoint) {
          const text = await res.text();
          try { return JSON.parse(text); } catch { return null; }
        }
      }
      if (res.status === 429) {
        const text429 = await res.text();
        try { return JSON.parse(text429); } catch { return { error: 'Trop de tentatives. Réessayez dans 15 minutes.' }; }
      }
      if (res.status === 409) {
        const text409 = await res.text();
        try { return JSON.parse(text409); } catch { return null; }
      }
      if (!res.ok) {
        // 400/403/500/etc. — on remonte le corps JSON s'il contient `{ error: "..." }`
        // pour que le caller propage un message explicite à l'utilisateur (au lieu de
        // tomber sur le fallback générique côté front).
        console.warn(`API error: ${res.status} ${res.statusText}`);
        try {
          const txt = await res.text();
          if (txt) {
            try {
              const body = JSON.parse(txt);
              if (body && (body.error || body.message)) return body;
            } catch { /* corps non-JSON, on tombe en null */ }
          }
        } catch { /* lecture impossible */ }
        return null;
      }
      const text = await res.text();
      if (!text || text.trim().startsWith('<') || (!text.trim().startsWith('{') && !text.trim().startsWith('['))) {
        console.warn('API returned non-JSON response');
        return null;
      }
      return JSON.parse(text);
    } catch (fetchError: any) {
      if (fetchError.name === 'AbortError') {
        console.warn('API timeout after 3s');
      } else {
        console.warn('API fetch error:', fetchError.message);
      }
      return null;
    }
  } catch (e) {
    return null;
  }
};

const safeFetchRaw = async (url: string): Promise<Response> => {
  const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN);
  return fetch(url, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

const mapLeadFromApi = (l: any): Lead => {
  if (!l) throw new Error("Donnée invalide");
  return {
    id: String(l.id), 
    companyId: String(l.company_id || l.companyId || ''),
    clientCompanyName: String(l.client_company_name || l.clientCompanyName || ''),
    tag: String(l.tag || ''),
    sectorActivity: String(l.sector_activity || l.sectorActivity || ''),
    name: String(l.name || 'Prospect Sans Nom'),
    email: String(l.email || ''),
    phone: String(l.phone || ''),
    phoneLandline: String(l.phone_landline || l.phoneLandline || ''),
    status: l.status || 'new',
    assignedTo: String(l.assigned_to || l.assignedTo || ''),
    isFavorite: l.isFavorite === true || l.isFavorite === 'true' || l.is_favorite === 1 || l.is_favorite === '1',
    additionalInfo: String(l.additionalInfo || l.additional_info || ''),
    potentialRevenue: String(l.potentialRevenue || l.potential_revenue || ''),
    createdAt: String(l.created_at || l.createdAt || new Date().toISOString()),
    source: String(l.source || ''),
    erpCustomerId: l.erp_customer_id || l.erpCustomerId || undefined,
    customFields: (() => {
      const cf = l.custom_fields || l.customFields;
      if (!cf) return undefined;
      if (typeof cf === 'string') { try { return JSON.parse(cf); } catch { return undefined; } }
      return cf;
    })(),
  };
};

// MySQL DATETIME ne porte pas d'info de fuseau ; le frontend envoie en UTC
// (toISOString avec 'Z') et PHP supprime le 'Z' pour stocker en MySQL.
// Au retour il faut donc forcer l'interprétation UTC sinon JS lit en local
// et décale de l'offset du fuseau (ex. CEST → -2h en avril).
/**
 * Convertit une ligne user renvoyée par l'API (snake_case partiel) vers
 * le type User côté front (camelCase). Centralise ce mapping pour éviter
 * la duplication dans login / restoreSession / loginTotp.
 */
const mapUserFromApi = (data: any): User => ({
  id: data.id,
  companyId: data.company_id || 'system',
  name: data.name,
  email: data.email,
  role: data.role,
  inactivityTimeout: data.inactivity_timeout != null ? Number(data.inactivity_timeout) : undefined,
  totpEnabled: !!(data.totp_enabled ?? data.totpEnabled),
  totpEnrolledAt: data.totp_enrolled_at ?? data.totpEnrolledAt ?? null,
});

export const normalizeApptDate = (s: string): string => {
  if (!s) return '';
  if (/[Zz]$|[+\-]\d{2}:?\d{2}$/.test(s)) return s; // déjà un fuseau
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(s)) {
    return s.replace(' ', 'T') + 'Z';
  }
  return s;
};

export const mapAppointmentFromApi = (a: any): Appointment => ({
  id: String(a.id),
  leadId: String(a.lead_id || a.leadId || ''),
  userId: String(a.user_id || a.userId || ''),
  title: String(a.title || ''),
  description: String(a.description || ''),
  start: normalizeApptDate(String(a.start_time || a.start || '')),
  end: normalizeApptDate(String(a.end_time || a.end || '')),
  type: (a.type || AppointmentType.PHONE) as AppointmentType,
  color: a.color ?? null,
  clientEmail: a.client_email || a.clientEmail || undefined,
  reminders: Array.isArray(a.reminders) ? a.reminders.map((r: any) => ({
    id: r.id ? String(r.id) : undefined,
    type: (r.type === 'email' ? 'email' : 'notification'),
    minutesBefore: Number(r.minutesBefore ?? r.minutes_before ?? 0),
    sentAt: r.sentAt ?? r.sent_at ?? null,
  })) : []
});

const mapCommentFromApi = (c: any): LeadComment => ({
  id: String(c.id),
  leadId: String(c.lead_id || c.leadId || ''),
  userId: String(c.user_id || c.userId || ''),
  userName: String(c.user_name || c.userName || ''),
  content: String(c.content || ''),
  createdAt: String(c.created_at || c.createdAt || new Date().toISOString())
});

const parseMaybeJson = (value: any) => {
  if (!value) return undefined;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const mapActivityFromApi = (a: any): LeadActivity => ({
  id: String(a.id),
  leadId: String(a.lead_id || a.leadId || ''),
  userId: String(a.user_id || a.userId || ''),
  userName: String(a.user_name || a.userName || ''),
  type: (a.type || LeadActivityType.INFO_UPDATED) as LeadActivityType,
  description: String(a.description || ''),
  metadata: parseMaybeJson(a.metadata),
  createdAt: String(a.created_at || a.createdAt || new Date().toISOString())
});

const mapOpportunityItemFromApi = (i: any, fallbackOpportunityId?: string): OpportunityItem => ({
  id: String(i.id || ''),
  opportunityId: String(i.opportunity_id || i.opportunityId || fallbackOpportunityId || ''),
  label: String(i.label || ''),
  quantity: Number(i.quantity ?? 0),
  unitPrice: Number(i.unit_price ?? i.unitPrice ?? 0),
  totalPrice: Number(i.total_price ?? i.totalPrice ?? 0)
});

const mapOpportunityFromApi = (o: any): Opportunity => ({
  id: String(o.id || ''),
  companyId: String(o.company_id || o.companyId || ''),
  leadId: o.lead_id || o.leadId ? String(o.lead_id || o.leadId) : undefined,
  clientCompanyName: String(o.client_company_name || o.clientCompanyName || ''),
  subject: String(o.subject || ''),
  comment: String(o.comment || ''),
  totalAmount: Number(o.total_amount ?? o.totalAmount ?? 0),
  createdBy: String(o.created_by || o.createdBy || ''),
  createdAt: String(o.created_at || o.createdAt || new Date().toISOString()),
  items: Array.isArray(o.items) ? o.items.map((i: any) => mapOpportunityItemFromApi(i, o.id)) : []
});

const mapTaskFromApi = (t: any): Task => ({
  id: String(t.id || ''),
  companyId: String(t.company_id || t.companyId || ''),
  leadId: t.lead_id || t.leadId ? String(t.lead_id || t.leadId) : undefined,
  clientCompanyName: t.client_company_name || t.clientCompanyName || undefined,
  title: String(t.title || ''),
  comment: String(t.comment || ''),
  dueAt: String(t.due_at || t.dueAt || new Date().toISOString()),
  status: (t.status === 'done' ? 'done' : 'todo'),
  createdBy: String(t.created_by || t.createdBy || ''),
  createdAt: String(t.created_at || t.createdAt || new Date().toISOString()),
  updatedAt: t.updated_at || t.updatedAt || undefined,
  completedAt: t.completed_at || t.completedAt || undefined
});

export const api = {
  // ===== AUTHENTIFICATION =====
  
  async restoreSession(): Promise<User | null> {
    const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN);
    if (!token) return null;
    try {
      const data = await safeFetch(`${API_BASE}?action=me`);
      if (data && data.id && !data.error) {
        return mapUserFromApi(data);
      }
    } catch {
      // token invalide ou expiré — la session ne peut pas être restaurée
    }
    return null;
  },

  async login(email: string, password?: string): Promise<LoginResult | null> {
    const cleanEmail = email.trim().toLowerCase();

    try {
      const data = await safeFetch(`${API_BASE}?action=login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail, password })
      });

      if (data && data.error) {
        throw new Error(data.error);
      }
      // Compte avec 2FA activé : étape 1 réussie, attente du code à 6 chiffres
      if (data && data.requireTotp && data.pendingToken) {
        const totpMethod = (data.totpMethod === 'email') ? 'email' : 'app';
        return { kind: 'totp_required', pendingToken: data.pendingToken, totpMethod };
      }
      if (data && !data.error && data.id) {
        if (data.token) {
          sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, data.token);
          localStorage.removeItem('auth_token'); // nettoyage ancienne clé
        }
        return { kind: 'success', user: mapUserFromApi(data) };
      }
    } catch (e) {
      console.log('API PHP non disponible');
    }

    if (!ALLOW_LOCAL_AUTH_FALLBACK) {
      return null;
    }

    const localUser = sessionUsers.find(u => u.email.toLowerCase() === cleanEmail);

    if (localUser) {
      const storedPass = localUser.password || '123';
      if (password === storedPass || (!password && storedPass === '123')) {
        console.log('✓ Connexion via stockage local réussie');
        return { kind: 'success', user: localUser };
      }
    }

    return null;
  },

  /**
   * Étape 2 du login pour les comptes avec 2FA activé. Échange le pendingToken
   * (émis par /login) + un code TOTP (6 chiffres) ou un code de secours
   * contre le user complet. Lance une erreur si le code est invalide ou si
   * le pendingToken a expiré (5 min).
   */
  async loginTotp(pendingToken: string, code: string): Promise<User> {
    const data = await safeFetch(`${API_BASE}?action=login_totp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pendingToken, code }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Code invalide');
    if (data.token) {
      sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, data.token);
    }
    return mapUserFromApi(data);
  },

  /**
   * Étape 2 du login pour les comptes avec 2FA email. Le code à 6 chiffres
   * a été envoyé automatiquement par /login lors de l'étape 1.
   */
  async loginEmailOtp(pendingToken: string, code: string): Promise<User> {
    const data = await safeFetch(`${API_BASE}?action=login_email_otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pendingToken, code }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Code invalide');
    if (data.token) {
      sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, data.token);
    }
    return mapUserFromApi(data);
  },

  // ===== 2FA TOTP =====

  /** Étape 1 d'enrôlement : récupère un secret + URI de provisioning. */
  async totpEnrollInit(): Promise<{ secret: string; provisioningUri: string }> {
    const data = await safeFetch(`${API_BASE}?action=totp_enroll_init`, {
      method: 'POST',
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur init 2FA');
    return { secret: data.secret, provisioningUri: data.provisioningUri };
  },

  /** Étape 2 d'enrôlement : vérifie le code et active le 2FA. Retourne les 8 codes de secours en clair (à sauvegarder par l'user). */
  async totpEnrollConfirm(secret: string, code: string): Promise<{ backupCodes: string[] }> {
    const data = await safeFetch(`${API_BASE}?action=totp_enroll_confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret, code }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur activation 2FA');
    return { backupCodes: data.backupCodes || [] };
  },

  /** Étape 1 d'enrôlement 2FA email : déclenche l'envoi d'un code 6 chiffres à l'email du user. Password requis. */
  async totpEmailEnrollInit(password: string): Promise<{ success: boolean }> {
    const data = await safeFetch(`${API_BASE}?action=totp_email_enroll_init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur envoi code');
    return { success: !!data.success };
  },

  /** Étape 2 d'enrôlement 2FA email : valide le code et active la méthode. */
  async totpEmailEnrollConfirm(code: string): Promise<{ success: boolean }> {
    const data = await safeFetch(`${API_BASE}?action=totp_email_enroll_confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Code invalide');
    return { success: !!data.success };
  },

  /** Désactive le 2FA après vérification du mot de passe. */
  async totpDisable(password: string): Promise<void> {
    const data = await safeFetch(`${API_BASE}?action=totp_disable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur désactivation 2FA');
  },

  /** Régénère 8 nouveaux codes de secours (les anciens sont invalidés). */
  async totpRegenerateBackupCodes(password: string): Promise<{ backupCodes: string[] }> {
    const data = await safeFetch(`${API_BASE}?action=totp_regenerate_backup_codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur régénération');
    return { backupCodes: data.backupCodes || [] };
  },

  // ===== GESTION UTILISATEURS =====

  async createUser(userData: Omit<User, 'id'> & { password?: string }): Promise<User> {
    // Pas de "fire-and-forget" — on attend la confirmation API et on remonte
    // les erreurs au caller (limite atteinte, email déjà utilisé, etc.).
    const result = await safeFetch(`${API_BASE}?action=create_user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    if (!result || result.error) {
      throw new Error(result?.error || 'Erreur lors de la création de l\'utilisateur');
    }
    const newUser = {
      ...userData,
      id: result.id || ('u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)),
      password: userData.password,
    };
    sessionUsers = [...sessionUsers, newUser];
    saveToStorage();
    return newUser;
  },

  async updateUser(userId: string, updates: Partial<User> & { password?: string }): Promise<User> {
    // L'appel API est SYSTÉMATIQUE — ne pas se baser sur le cache local
    // (sessionUsers) pour décider, sinon les users "system" (SUPER_ADMIN,
    // SUPPORT_USER, companyId='system') et tout user absent du cache ne
    // pourraient jamais être modifiés.
    const result = await safeFetch(`${API_BASE}?action=update_user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: userId, ...updates })
    });
    if (result && result.error) {
      throw new Error(result.error);
    }

    // Mettre à jour le cache local SI l'user y était (optimisation d'affichage).
    const index = sessionUsers.findIndex(u => String(u.id) === String(userId));
    if (index !== -1) {
      sessionUsers[index] = {
        ...sessionUsers[index],
        ...updates,
        ...(updates.password && { password: updates.password })
      };
      saveToStorage();
      return sessionUsers[index];
    }

    // User hors cache (ex: system users) : reconstruire un objet à partir
    // des updates + l'id pour que l'appelant puisse rafraîchir son état local.
    return { id: userId, ...updates } as User;
  },

  async deleteUser(userId: string): Promise<void> {
    sessionUsers = sessionUsers.filter(u => String(u.id) !== String(userId));
    saveToStorage();
    
    await safeFetch(`${API_BASE}?action=delete_user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: userId })
    });
  },

  async getSuperAdmins(): Promise<User[]> {
    if (isDbMode) {
      try {
        const apiData = await safeFetch(`${API_BASE}?action=get_system_users`);
        if (Array.isArray(apiData)) {
          return apiData.map((u: any) => ({
            id: String(u.id),
            companyId: String(u.company_id || 'system'),
            name: u.name,
            email: u.email,
            role: u.role as UserRole,
          }));
        }
      } catch (e) {
        console.warn('get_system_users failed, fallback local');
      }
    }
    return sessionUsers.filter(u => u.role === UserRole.SUPER_ADMIN);
  },

  async getCompanyUsers(companyId: string): Promise<User[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_company_users&company_id=${companyId}`);
      if (Array.isArray(apiData)) {
        const mappedUsers = apiData.map((u: any) => ({
          id: String(u.id),
          companyId: String(u.company_id || u.companyId || ''),
          name: String(u.name || ''),
          email: String(u.email || ''),
          role: u.role || UserRole.USER,
          managerId: u.manager_id || u.managerId || undefined,
          inactivityTimeout: u.inactivity_timeout !== null && u.inactivity_timeout !== undefined
            ? Number(u.inactivity_timeout)
            : undefined,
          userFeatures: (() => {
            const uf = u.user_features || u.userFeatures;
            if (!uf) return undefined;
            if (typeof uf === 'string') { try { return JSON.parse(uf); } catch { return undefined; } }
            return typeof uf === 'object' ? uf : undefined;
          })(),
          leadSheetTemplateId: u.lead_sheet_template_id ?? u.leadSheetTemplateId ?? null,
          customRoleId: u.custom_role_id ?? u.customRoleId ?? null,
        }));
        sessionUsers = [
          ...sessionUsers.filter(u => String(u.companyId) !== String(companyId)),
          ...mappedUsers
        ];
        return mappedUsers;
      }
      // L'API a échoué — lever une erreur pour que l'appelant puisse réessayer
      throw new Error('Impossible de charger les collaborateurs depuis la base de données');
    }
    return sessionUsers.filter(u => String(u.companyId) === String(companyId));
  },

  getLocalCompanyAdmin(companyId: string): User | undefined {
    return sessionUsers.find(u => 
      String(u.companyId) === String(companyId) && 
      u.role === UserRole.COMPANY_ADMIN
    );
  },

  // ===== CONFIGURATION SYSTÈME =====

  async getSystemConfig(): Promise<SystemConfig> {
    if (!sessionConfig.activitySectors || !Array.isArray(sessionConfig.activitySectors)) {
      sessionConfig = { ...sessionConfig, activitySectors: [...DEFAULT_ACTIVITY_SECTORS] };
      saveToStorage();
    }
    return sessionConfig;
  },

  async updateSystemConfig(newConfig: Partial<SystemConfig>): Promise<SystemConfig> {
    sessionConfig = { ...sessionConfig, ...newConfig };
    saveToStorage();
    return sessionConfig;
  },

  async getActivitySectors(): Promise<string[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_activity_sectors`);
      if (Array.isArray(apiData)) {
        const sectors = apiData
          .map((row: any) => String(row.name || row.sector_name || ''))
          .filter((name: string) => name.trim() !== '');
        if (sectors.length > 0) {
          sessionConfig = { ...sessionConfig, activitySectors: sectors };
          saveToStorage();
          return sectors;
        }
      }
    }

    if (!sessionConfig.activitySectors || sessionConfig.activitySectors.length === 0) {
      sessionConfig = { ...sessionConfig, activitySectors: [...DEFAULT_ACTIVITY_SECTORS] };
      saveToStorage();
    }

    return [...(sessionConfig.activitySectors || [])];
  },

  async addActivitySector(name: string): Promise<string[]> {
    const cleaned = String(name || '').trim();
    if (!cleaned) {
      throw new Error('Nom de secteur invalide');
    }

    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=create_activity_sector`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cleaned })
      });
      if (!apiResult || apiResult.success !== true) {
        throw new Error('Creation du secteur echouee cote base');
      }
      return await api.getActivitySectors();
    }

    const current = new Set((sessionConfig.activitySectors || []).map(s => s.trim()).filter(Boolean));
    current.add(cleaned);
    const next = Array.from(current).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
    sessionConfig = { ...sessionConfig, activitySectors: next };
    saveToStorage();
    return next;
  },

  // ===== GESTION ENTREPRISES =====
  
  async createCompany(companyData: Partial<Company> & { adminName: string, adminEmail: string, adminPassword?: string, gdprConsent?: boolean, gdprVersion?: string, dpaConsent?: boolean, dpaVersion?: string }): Promise<Company> {
    const generatedId = companyData.id || ('c_' + Date.now().toString(36));
    const newComp: Company = {
      id: generatedId,
      name: companyData.name || 'Nouvelle Entreprise',
      plan: companyData.plan || SubscriptionPlan.BASIC,
      userLimit: companyData.userLimit || 3,
      activeLeads: 0,
      status: 'active',
      createdAt: new Date().toISOString(),
      currency: companyData.currency || 'EUR'
    };
    
    const newAdmin = {
      id: 'u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      companyId: generatedId,
      name: companyData.adminName,
      email: companyData.adminEmail,
      role: UserRole.COMPANY_ADMIN,
      password: companyData.adminPassword || '123456'
    };

    sessionCompanies = [newComp, ...sessionCompanies];
    sessionUsers = [...sessionUsers, newAdmin];
    saveToStorage();

    const result = await safeFetch(`${API_BASE}?action=create_company_full`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...companyData,
        id: generatedId,
        gdprConsent: companyData.gdprConsent ?? false,
        gdprVersion: companyData.gdprVersion ?? '1.0',
        dpaConsent: companyData.dpaConsent ?? false,
        dpaVersion: companyData.dpaVersion ?? '1.0'
      })
    });

    if (!result || result.error) {
      // Retirer la compagnie de la session locale si l'API échoue
      sessionCompanies = sessionCompanies.filter(c => c.id !== generatedId);
      throw new Error(result?.error || 'Erreur lors de la création de l\'entreprise');
    }

    return newComp;
  },

  async recordGdprConsent(userId: string, version = '1.0'): Promise<void> {
    try {
      await safeFetch(`${API_BASE}?action=record_gdpr_consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, version })
      });
    } catch (e) {
      console.warn('Erreur enregistrement consentement RGPD', e);
    }
  },

  async gdprExportUser(userId: string): Promise<void> {
    const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN);
    const res = await fetch(`${API_BASE}?action=gdpr_export_user&user_id=${encodeURIComponent(userId)}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) {
      let msg = `Export échoué (HTTP ${res.status})`;
      try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mes_donnees_orion_crm_${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  async gdprEraseUser(userId: string): Promise<void> {
    const data = await safeFetch(`${API_BASE}?action=gdpr_erase_user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId })
    });
    if (data?.error) throw new Error(data.error);
  },

  async getCompanies(): Promise<Company[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_companies`);
      if (Array.isArray(apiData)) {
        const mappedCompanies: Company[] = apiData.map((c: any) => {
          let revenueBrackets: string[] | undefined;
          let revenueColors: Record<string, string> | undefined;
          try { revenueBrackets = c.revenue_brackets ? JSON.parse(c.revenue_brackets) : undefined; } catch { revenueBrackets = undefined; }
          try { revenueColors = c.revenue_colors ? JSON.parse(c.revenue_colors) : undefined; } catch { revenueColors = undefined; }
          return {
            id: String(c.id),
            name: String(c.name || ''),
            plan: c.plan || SubscriptionPlan.BASIC,
            userLimit: Number(c.user_limit ?? c.userLimit ?? 3),
            activeLeads: Number(c.active_leads ?? c.activeLeads ?? 0),
            status: c.status || 'active',
            createdAt: String(c.created_at || c.createdAt || new Date().toISOString()),
            currency: String(c.currency || 'EUR'),
            timezone: String(c.timezone || 'Europe/Paris'),
            address: c.address || undefined,
            phone: c.phone || undefined,
            email: c.email || undefined,
            website: c.website || undefined,
            revenueBrackets: Array.isArray(revenueBrackets) && revenueBrackets.length > 0 ? revenueBrackets : undefined,
            revenueColors: revenueColors && Object.keys(revenueColors).length > 0 ? revenueColors : undefined,
            cardBgColor: c.card_bg_color || c.cardBgColor || undefined,
            cardNameColor: c.card_name_color || c.cardNameColor || undefined,
            cardEmailColor: c.card_email_color || c.cardEmailColor || undefined,
            cardBgColorLight: c.card_bg_color_light || c.cardBgColorLight || undefined,
            cardNameColorLight: c.card_name_color_light || c.cardNameColorLight || undefined,
            cardEmailColorLight: c.card_email_color_light || c.cardEmailColorLight || undefined,
            maskContacts: !!(c.mask_contacts || c.maskContacts),
            customStatuses: (() => {
              const cs = c.custom_statuses || c.customStatuses;
              if (!cs) return undefined;
              if (typeof cs === 'string') { try { return JSON.parse(cs); } catch { return undefined; } }
              return Array.isArray(cs) ? cs : undefined;
            })(),
            customFieldDefinitions: (() => {
              const cfd = c.custom_field_definitions || c.customFieldDefinitions;
              if (!cfd) return undefined;
              if (typeof cfd === 'string') { try { return JSON.parse(cfd); } catch { return undefined; } }
              return Array.isArray(cfd) ? cfd : undefined;
            })(),
            inactivityTimeout: c.inactivity_timeout !== null && c.inactivity_timeout !== undefined
              ? Number(c.inactivity_timeout)
              : (c.inactivityTimeout !== undefined ? Number(c.inactivityTimeout) : undefined),
            userFeatures: (() => {
              const uf = c.user_features || c.userFeatures;
              if (!uf) return undefined;
              if (typeof uf === 'string') { try { return JSON.parse(uf); } catch { return undefined; } }
              return typeof uf === 'object' ? uf : undefined;
            })(),
            cardConfig: (() => {
              const cc = c.card_config || c.cardConfig;
              if (!cc) return undefined;
              if (typeof cc === 'string') { try { return JSON.parse(cc); } catch { return undefined; } }
              return typeof cc === 'object' ? cc : undefined;
            })(),
            leadCreationMode: (c.lead_creation_mode === 'full' || c.leadCreationMode === 'full') ? 'full' : 'short'
          };
        });
        sessionCompanies = mappedCompanies;
        return mappedCompanies;
      }
      return [];
    }
    return sessionCompanies;
  },

  async updateCompany(companyId: string, updates: Partial<Company>): Promise<Company> {
    let index = sessionCompanies.findIndex(c => String(c.id) === String(companyId));
    if (index === -1) {
      // Company pas en session — on la crée à partir des updates
      sessionCompanies.push({ id: companyId, name: '', plan: 'BASIC', userLimit: 3, activeLeads: 0, status: 'active', createdAt: new Date().toISOString(), ...updates } as Company);
      index = sessionCompanies.length - 1;
    } else {
      sessionCompanies[index] = { ...sessionCompanies[index], ...updates };
    }
    saveToStorage();

    const result = await safeFetch(`${API_BASE}?action=update_company`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: companyId, ...updates })
    });
    if (result && result.error) {
      throw new Error(result.error);
    }

    return sessionCompanies[index];
  },

  async updateCompanyStatus(id: string, status: 'active' | 'suspended'): Promise<void> {
    sessionCompanies = sessionCompanies.map(c => c.id === id ? { ...c, status } : c);
    saveToStorage();
    await safeFetch(`${API_BASE}?action=update_company_status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, status })
    });
  },

  async getUserPrefs(): Promise<{ dashboardLayoutDesktop: any | null; dashboardLayoutMobile: any | null; onboardingDismissed: boolean } | null> {
    const result = await safeFetch(`${API_BASE}?action=get_user_prefs`);
    if (!result || result.error) return null;
    return result;
  },

  // `layout` est soit un tableau plat (ancien format),
  // soit un objet `{ byCols: { [cols]: WidgetSpec[] } }` (nouveau format
  // multi-résolution — un layout par nombre de colonnes).
  async saveDashboardLayout(variant: 'desktop' | 'mobile', layout: any[] | { byCols: Record<string, any[]> }): Promise<void> {
    await safeFetch(`${API_BASE}?action=save_dashboard_layout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variant, layout })
    });
  },

  async dismissOnboarding(): Promise<void> {
    await safeFetch(`${API_BASE}?action=dismiss_onboarding`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
  },

  async getLeadSheetTemplates(companyId?: string): Promise<LeadSheetTemplate[]> {
    const qs = companyId ? `&company_id=${encodeURIComponent(companyId)}` : '';
    const result = await safeFetch(`${API_BASE}?action=get_lead_sheet_templates${qs}`);
    if (!Array.isArray(result)) return [];
    return result as LeadSheetTemplate[];
  },

  async saveLeadSheetTemplate(tpl: Partial<LeadSheetTemplate> & { name: string; config: any; isDefault?: boolean; companyId?: string }): Promise<{ id: string }> {
    const result = await safeFetch(`${API_BASE}?action=save_lead_sheet_template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tpl)
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur enregistrement modèle');
    return { id: result.id };
  },

  async deleteLeadSheetTemplate(id: string): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=delete_lead_sheet_template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur suppression modèle');
  },

  async assignLeadSheetTemplate(userId: string, templateId: string | null): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=assign_lead_sheet_template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, templateId })
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur affectation');
  },

  async getMyLeadSheetTemplate(): Promise<LeadSheetTemplate | null> {
    const result = await safeFetch(`${API_BASE}?action=get_my_lead_sheet_template`);
    if (!result || result.error) return null;
    return result as LeadSheetTemplate;
  },

  async deleteCompany(id: string): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=delete_company`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    });
    if (!result || result.error) {
      throw new Error(result?.error || "Erreur lors de la suppression de l'entreprise");
    }
    sessionCompanies = sessionCompanies.filter(c => c.id !== id);
    sessionUsers = sessionUsers.filter(u => u.companyId !== id);
    saveToStorage();
  },

  // ===== GESTION PROSPECTS =====

  async getLeads(params: { companyId: string, userId: string, role: string, agentId?: string, dateFrom?: string, dateTo?: string }): Promise<Lead[]> {
    const apiData = await safeFetch(`${API_BASE}?action=get_leads&company_id=${params.companyId}`);
    if (Array.isArray(apiData)) {
      const mappedLeads = apiData.map(mapLeadFromApi);
      sessionLeads = [
        ...mappedLeads.filter(l => String(l.companyId) === String(params.companyId)),
        ...sessionLeads.filter(l => String(l.companyId) !== String(params.companyId))
      ];
    } else if (isDbMode) {
      throw new Error('Impossible de charger les prospects depuis la base');
    }
    
    // 2. FILTRER (depuis localStorage maintenant synchronisé)
    let filtered = sessionLeads.filter(l => String(l.companyId) === String(params.companyId));
    if (params.role === UserRole.USER) {
      filtered = filtered.filter(l => String(l.assignedTo) === String(params.userId));
    } else if (params.role === UserRole.MANAGER) {
      const teamIds = sessionUsers
        .filter(u => String(u.managerId) === String(params.userId) || String(u.id) === String(params.userId))
        .map(u => String(u.id));
      filtered = filtered.filter(l => teamIds.includes(String(l.assignedTo)));
    } else if (params.agentId && params.agentId !== 'all') {
      filtered = filtered.filter(l => String(l.assignedTo) === String(params.agentId));
    }
    
    if (params.dateFrom) {
      filtered = filtered.filter(l => new Date(l.createdAt) >= new Date(params.dateFrom!));
    }
    if (params.dateTo) {
      filtered = filtered.filter(l => new Date(l.createdAt) <= new Date(params.dateTo!));
    }

    return filtered.map(mapLeadFromApi);
  },

  /**
   * Variante paginée de getLeads. À utiliser pour les vues qui peuvent
   * fonctionner par tranches (liste, infinite scroll). Retourne `total`
   * pour permettre l'affichage du compteur global.
   */
  async getLeadsPaginated(params: { companyId: string; page?: number; limit?: number }): Promise<PaginatedResult<Lead>> {
    const page  = params.page  ?? 1;
    const limit = params.limit ?? 50;

    if (!isDbMode) {
      // Demo : pagine côté client à partir de sessionLeads.
      const filtered = sessionLeads.filter(l => String(l.companyId) === String(params.companyId));
      const start = (page - 1) * limit;
      return { items: filtered.slice(start, start + limit), total: filtered.length, page, limit };
    }

    const url = `${API_BASE}?action=get_leads&company_id=${encodeURIComponent(params.companyId)}&page=${page}&limit=${limit}`;
    const apiData = await safeFetch(url);
    if (!apiData || !Array.isArray(apiData.items)) {
      throw new Error('Réponse paginée inattendue pour get_leads');
    }
    return {
      items: apiData.items.map(mapLeadFromApi),
      total: Number(apiData.total) || 0,
      page: Number(apiData.page) || page,
      limit: Number(apiData.limit) || limit,
    };
  },

  async createLead(leadData: Partial<Lead> & { name: string, email: string, companyId: string }, currentUser?: { id: string, name: string }): Promise<Lead> {
    const generatedId = leadData.id || ('lead_' + Date.now().toString(36));
    const fullLead: Lead = {
      id: generatedId,
      companyId: String(leadData.companyId || ''),
      clientCompanyName: String(leadData.clientCompanyName || ''),
      tag: String(leadData.tag || ''),
      sectorActivity: String(leadData.sectorActivity || ''),
      name: String(leadData.name || 'Nouveau Prospect'),
      email: String(leadData.email || ''),
      phone: String(leadData.phone || ''),
      phoneLandline: String(leadData.phoneLandline || ''),
      status: leadData.status || 'new',
      assignedTo: String(leadData.assignedTo || ''),
      createdAt: leadData.createdAt || new Date().toISOString(),
      potentialRevenue: leadData.potentialRevenue || '',
      additionalInfo: leadData.additionalInfo || '',
      isFavorite: leadData.isFavorite || false,
      source: leadData.source || '',
      customFields: leadData.customFields || {}
    };

    // `forceDuplicate` permet à l'utilisateur de bypasser le contrôle anti-doublon
    // après avoir confirmé un prompt. On le transmet au backend qui le respectera.
    const forceDuplicate = (leadData as any).forceDuplicate === true;
    const apiResult = await safeFetch(`${API_BASE}?action=create_lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...fullLead, ...(forceDuplicate ? { forceDuplicate: true } : {}) })
    });

    if (apiResult?.warning === 'duplicate') {
      const dup = apiResult.duplicate;
      const dupInfo = `${dup.name} (${dup.email || dup.phone})`;
      throw new Error(`DUPLICATE:${dupInfo}`);
    }
    if (apiResult?.error) {
      // Le backend a renvoyé une erreur métier explicite (validation, RGPD, etc.) — on la propage telle quelle.
      throw new Error(apiResult.error);
    }
    if (!apiResult || (!apiResult.success && !apiResult.id)) {
      if (isDbMode) {
        throw new Error('Création du prospect échouée côté base de données');
      }
      console.warn('⚠️  API a pas confirmé la création, stockage local seul');
    } else {
      console.log(`✓ Prospect "${fullLead.name}" créé en BD (API)`);
      // Le backend peut avoir auto-assigné le prospect via les règles d'affectation —
      // on reflète la décision serveur dans l'objet local avant retour.
      if (apiResult.assignedTo !== undefined) {
        fullLead.assignedTo = String(apiResult.assignedTo ?? '');
      }
    }

    sessionLeads = [fullLead, ...sessionLeads];
    
    // 3. ACTIVITÉ : Création du prospect
    if (currentUser) {
      createActivity(
        generatedId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.CREATED,
        `Prospect créé par ${currentUser.name}`
      );
    }

    // 4. NOTIFICATION : Prospect assigné
    if (fullLead.assignedTo && fullLead.assignedTo !== currentUser?.id) {
      await api.createNotification({
        userId: fullLead.assignedTo,
        type: NotificationType.LEAD_ASSIGNED,
        title: 'Nouveau prospect assigné',
        message: `${currentUser?.name || 'Un administrateur'} vous a assigné un nouveau prospect`,
        leadId: fullLead.id,
        leadName: fullLead.name,
        isRead: false
      });
    }
    
    saveToStorage();
    return fullLead;
  },

  async updateLead(leadId: string, updates: Partial<Lead>, currentUser?: { id: string, name: string }): Promise<Lead> {
    const index = sessionLeads.findIndex(l => String(l.id) === String(leadId));
    if (index !== -1) {
      const oldLead = sessionLeads[index];
      sessionLeads[index] = { ...sessionLeads[index], ...updates };
      
      // ACTIVITÉS
      if (currentUser) {
        // Changement de statut
        if (updates.status && updates.status !== oldLead.status) {
          const oldLabel = updates.status ? (oldLead.status || '?') : '?';
          const newLabel = updates.status || '?';
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.STATUS_CHANGED,
            `Statut changé de "${oldLabel}" à "${newLabel}"`,
            {
              oldValue: oldLabel,
              newValue: newLabel
            }
          );
        }
        
        // Changement d'assignation
        if (updates.assignedTo && updates.assignedTo !== oldLead.assignedTo) {
          const oldUser = sessionUsers.find(u => u.id === oldLead.assignedTo);
          const newUser = sessionUsers.find(u => u.id === updates.assignedTo);
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.ASSIGNED,
            `Prospect réassigné à ${newUser?.name || 'Inconnu'}`,
            {
              oldValue: oldUser?.name || 'Non assigné',
              newValue: newUser?.name || 'Inconnu'
            }
          );

          // NOTIFICATION : Réassignation
          if (updates.assignedTo !== currentUser.id) {
            await api.createNotification({
              userId: updates.assignedTo,
              type: NotificationType.LEAD_ASSIGNED,
              title: 'Prospect réassigné',
              message: `${currentUser.name} vous a assigné le prospect ${oldLead.name}`,
              leadId: leadId,
              leadName: oldLead.name,
              isRead: false
            });
          }
        }
        
        // Toggle favori
        if (updates.isFavorite !== undefined && updates.isFavorite !== oldLead.isFavorite) {
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.FAVORITE_TOGGLED,
            updates.isFavorite ? 'Ajouté aux favoris' : 'Retiré des favoris'
          );
        }
        
        // Diff complet des champs modifiés — chaque changement est journalisé.
        const fieldDiffs: { key: keyof Lead; label: string }[] = [
          { key: 'name', label: 'Nom' },
          { key: 'clientCompanyName', label: 'Entreprise' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Tél. mobile' },
          { key: 'phoneLandline', label: 'Tél. fixe' },
          { key: 'tag', label: 'Tag' },
          { key: 'sectorActivity', label: 'Secteur' },
          { key: 'potentialRevenue', label: 'Revenu potentiel' },
          { key: 'source', label: 'Source' },
          { key: 'additionalInfo', label: 'Notes' },
        ];
        const changedFields: string[] = [];
        for (const { key, label } of fieldDiffs) {
          if (!(key in updates)) continue;
          const next = (updates as any)[key];
          const prev = (oldLead as any)[key];
          if (next === undefined) continue;
          const a = prev == null ? '' : String(prev);
          const b = next == null ? '' : String(next);
          if (a === b) continue;
          changedFields.push(`${label}: "${a}" → "${b}"`);
        }
        if (changedFields.length > 0) {
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.INFO_UPDATED,
            changedFields.join(' | ')
          );
        }
      }
      
      saveToStorage();

      // SYNC API — attendre la confirmation
      try {
        const result = await safeFetch(`${API_BASE}?action=update_lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: leadId, ...updates })
        });
        if (result && result.success) {
          console.log(`✓ Prospect "${sessionLeads[index].name}" mis à jour en BD (API)`);
        } else {
          console.warn('⚠️ Sync API updateLead: réponse inattendue', result);
        }
      } catch (err: any) {
        console.warn('⚠️ Erreur sync API pour updateLead:', err.message);
      }
      
      return sessionLeads[index];
    }
    throw new Error(`Lead ${leadId} non trouvé`);
  },

  async deleteLead(leadId: string): Promise<void> {
    if (isDbMode) {
      try {
        const result = await safeFetch(`${API_BASE}?action=delete_lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: leadId })
        });
        if (!result || result.error) {
          console.warn('⚠️ Suppression API échouée:', result?.error || 'pas de réponse');
        }
      } catch (e) {
        console.warn('⚠️ Erreur réseau suppression:', e);
      }
    }
    sessionLeads = sessionLeads.filter(l => l.id !== leadId);
    sessionActivities = sessionActivities.filter(a => a.leadId !== leadId);
    sessionAppointments = sessionAppointments.filter(a => a.leadId !== leadId);
    saveToStorage();
  },

  // ===== ACTIONS EN MASSE =====

  async bulkUpdateLeads(ids: string[], field: 'status' | 'assigned_to', value: string): Promise<{ updated: number }> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=bulk_update_leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, field, value })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Mise à jour en masse échouée');
      }
      return result;
    }
    // local fallback
    ids.forEach(id => {
      const lead = sessionLeads.find(l => l.id === id);
      if (lead) { (lead as any)[field === 'assigned_to' ? 'assignedTo' : field] = value; }
    });
    saveToStorage();
    return { updated: ids.length };
  },

  async bulkDeleteLeads(ids: string[]): Promise<{ deleted: number }> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=bulk_delete_leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Suppression en masse échouée');
      }
      return result;
    }
    sessionLeads = sessionLeads.filter(l => !ids.includes(l.id));
    saveToStorage();
    return { deleted: ids.length };
  },

  // ===== TACHES =====

  async getTasks(filters: { companyId: string; leadId?: string; status?: 'todo' | 'done' }): Promise<Task[]> {
    if (isDbMode) {
      const query: string[] = [`company_id=${encodeURIComponent(filters.companyId)}`];
      if (filters.leadId) query.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters.status) query.push(`status=${encodeURIComponent(filters.status)}`);
      const apiData = await safeFetch(`${API_BASE}?action=get_tasks&${query.join('&')}`);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapTaskFromApi);
        sessionTasks = [
          ...sessionTasks.filter(t => String(t.companyId) !== String(filters.companyId)),
          ...mapped
        ];
        saveToStorage();
        return mapped;
      }
      throw new Error('Impossible de recuperer les taches depuis la base');
    }

    return sessionTasks.filter(t => {
      if (String(t.companyId) !== String(filters.companyId)) return false;
      if (filters.leadId && String(t.leadId || '') !== String(filters.leadId)) return false;
      if (filters.status && t.status !== filters.status) return false;
      return true;
    });
  },

  /** Variante paginée de getTasks. */
  async getTasksPaginated(filters: { companyId: string; leadId?: string; status?: 'todo' | 'done'; page?: number; limit?: number }): Promise<PaginatedResult<Task>> {
    const page  = filters.page  ?? 1;
    const limit = filters.limit ?? 50;

    if (!isDbMode) {
      const filtered = sessionTasks.filter(t => {
        if (String(t.companyId) !== String(filters.companyId)) return false;
        if (filters.leadId && String(t.leadId || '') !== String(filters.leadId)) return false;
        if (filters.status && t.status !== filters.status) return false;
        return true;
      });
      const start = (page - 1) * limit;
      return { items: filtered.slice(start, start + limit), total: filtered.length, page, limit };
    }

    const query: string[] = [
      `company_id=${encodeURIComponent(filters.companyId)}`,
      `page=${page}`,
      `limit=${limit}`,
    ];
    if (filters.leadId) query.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
    if (filters.status) query.push(`status=${encodeURIComponent(filters.status)}`);
    const apiData = await safeFetch(`${API_BASE}?action=get_tasks&${query.join('&')}`);
    if (!apiData || !Array.isArray(apiData.items)) {
      throw new Error('Réponse paginée inattendue pour get_tasks');
    }
    return {
      items: apiData.items.map(mapTaskFromApi),
      total: Number(apiData.total) || 0,
      page: Number(apiData.page) || page,
      limit: Number(apiData.limit) || limit,
    };
  },

  async createTask(payload: {
    companyId: string;
    leadId?: string;
    clientCompanyName?: string;
    title: string;
    comment?: string;
    dueAt: string;
  }, currentUser: { id: string; name: string }): Promise<Task> {
    const id = 'task_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
    const task: Task = {
      id,
      companyId: payload.companyId,
      leadId: payload.leadId,
      clientCompanyName: payload.clientCompanyName || undefined,
      title: payload.title,
      comment: payload.comment || '',
      dueAt: payload.dueAt,
      status: 'todo',
      createdBy: currentUser.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=create_task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(task)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation de la tache echouee cote base');
      }
      const persisted = mapTaskFromApi(apiResult);
      sessionTasks = [persisted, ...sessionTasks];
      saveToStorage();
      return persisted;
    }

    sessionTasks = [task, ...sessionTasks];
    saveToStorage();
    return task;
  },

  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=update_task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: taskId, ...updates })
      });
      if (!apiResult || !apiResult.success || !apiResult.task) {
        throw new Error('Mise a jour de la tache echouee cote base');
      }
      const persisted = mapTaskFromApi(apiResult.task);
      sessionTasks = sessionTasks.map(t => t.id === taskId ? persisted : t);
      saveToStorage();
      return persisted;
    }

    const index = sessionTasks.findIndex(t => t.id === taskId);
    if (index === -1) throw new Error(`Tache ${taskId} non trouvee`);
    sessionTasks[index] = {
      ...sessionTasks[index],
      ...updates,
      updatedAt: new Date().toISOString(),
      completedAt: updates.status === 'done' ? new Date().toISOString() : (updates.status === 'todo' ? undefined : sessionTasks[index].completedAt)
    };
    saveToStorage();
    return sessionTasks[index];
  },

  async deleteTask(taskId: string): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=delete_task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: taskId })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Suppression de la tache echouee');
      }
    }
    sessionTasks = sessionTasks.filter(t => t.id !== taskId);
    saveToStorage();
  },

  async processAppointmentReminders(): Promise<{ processed: number }> {
    if (!isDbMode) return { processed: 0 };
    try {
      const res = await safeFetch(`${API_BASE}?action=process_notification_reminders`, { method: 'POST' });
      return { processed: (res && typeof res.processed === 'number') ? res.processed : 0 };
    } catch {
      return { processed: 0 };
    }
  },

  async checkUpcomingTaskReminders(userId: string): Promise<void> {
    const now = Date.now();
    const horizonMs = 1000 * 60 * 60 * 2; // 2h
    const upcoming = sessionTasks.filter(t => t.status === 'todo' && t.createdBy === userId);

    for (const task of upcoming) {
      const dueMs = new Date(task.dueAt).getTime();
      if (Number.isNaN(dueMs)) continue;
      const remaining = dueMs - now;
      if (remaining < 0 || remaining > horizonMs) continue;

      const existingNotif = sessionNotifications.find(n =>
        n.userId === userId &&
        n.type === NotificationType.TASK_DUE_SOON &&
        n.metadata?.taskId === task.id
      );
      if (existingNotif) continue;

      await api.createNotification({
        userId,
        type: NotificationType.TASK_DUE_SOON,
        title: 'Tache a echeance proche',
        message: `${task.title} arrive a echeance bientot`,
        leadId: task.leadId,
        leadName: task.clientCompanyName || undefined,
        isRead: false,
        metadata: { taskId: task.id, dueAt: task.dueAt }
      });
    }
  },

  // ===== ATTACHMENTS =====

  async uploadAttachment(leadId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('lead_id', leadId);
    formData.append('file', file);

    const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN);
    const res = await fetch(`${API_BASE}?action=upload_attachment`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    const text = await res.text();
    let data: any;
    try {
      data = JSON.parse(text);
    } catch {
      console.error('Upload response not JSON:', text);
      throw new Error('Erreur serveur lors de l\'upload');
    }
    if (data.error) throw new Error(data.error);
    return data;
  },

  async getAttachments(leadId: string): Promise<any[]> {
    if (!isDbMode) return [];
    const data = await safeFetch(`${API_BASE}?action=get_attachments&lead_id=${encodeURIComponent(leadId)}`);
    return Array.isArray(data) ? data : [];
  },

  async downloadAttachment(attId: string): Promise<void> {
    const response = await safeFetchRaw(`${API_BASE}?action=download_attachment&id=${encodeURIComponent(attId)}`);
    if (!response.ok) throw new Error('Echec du telechargement');
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'fichier';
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  async deleteAttachment(attId: string): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=delete_attachment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: attId })
    });
    if (result?.error) throw new Error(result.error);
  },

  // ===== PROFIL UTILISATEUR =====

  async updateProfile(data: { name?: string; email?: string; password?: string; current_password: string }): Promise<User> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=update_profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!result || result.error) throw new Error(result?.error || 'Erreur mise à jour profil');
      return { id: result.id, companyId: result.company_id, name: result.name, email: result.email, role: result.role } as User;
    }
    throw new Error('Non disponible en mode démo');
  },

  // ===== HEARTBEAT & SERVER STATS =====

  async heartbeat(): Promise<void> {
    if (!isDbMode) return;
    let sid = sessionStorage.getItem('orion_heartbeat_session_id');
    if (!sid) {
      sid = 'sess_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      sessionStorage.setItem('orion_heartbeat_session_id', sid);
    }
    try {
      await safeFetch(`${API_BASE}?action=heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid }),
        timeout: 5000,
      });
    } catch { /* silencieux : heartbeat best-effort */ }
  },

  async getServerStats(): Promise<{
    timestamp: string;
    sessions: {
      window_minutes: number;
      users_active: number;
      companies_active: number;
      by_company: Array<{ company_id: string; company_name: string; users: number }>;
    };
    totals: { users: number; companies: number };
    mysql: {
      Threads_connected: number; Threads_running: number; Uptime: number;
      Questions: number; Slow_queries: number;
      Innodb_buffer_pool_read_requests: number; Innodb_buffer_pool_reads: number;
      Max_used_connections: number;
    };
    innodb: {
      buffer_pool_read_requests: number;
      buffer_pool_reads: number;
      hit_rate_pct: number | null;
      max_connections: number;
    };
    database: { name: string; bytes: number };
    disk: { path: string; free_bytes: number | null; total_bytes: number | null };
    cpu_load: { '1min': number; '5min': number; '15min': number } | null;
    php_memory: { current_bytes: number; peak_bytes: number; limit: string };
    php_version: string;
    os: string;
  } | null> {
    if (!isDbMode) return null;
    try {
      return await safeFetch(`${API_BASE}?action=server_stats`);
    } catch { return null; }
  },

  async getServerStatsHistory(range: '1h' | '24h' | '7d' | '30d' = '1h'): Promise<{
    range: string;
    label: string;
    bucket_sec: number;
    buckets: Array<{
      bucket: string;
      users_avg: number | null; users_max: number | null;
      companies_avg: number | null;
      mysql_threads_avg: number | null; mysql_threads_max: number | null;
      api_p50_avg: number | null; api_p95_avg: number | null; api_p99_avg: number | null;
      api_requests_sum: number | null; api_errors_5xx_sum: number | null;
      cpu_avg: number | null;
      disk_free: number | null; db_size: number | null;
    }>;
    perf_global: {
      count: number;
      p50_ms: number | null; p95_ms: number | null; p99_ms: number | null;
      errors_5xx: number; errors_4xx: number;
      error_rate_5xx_pct: number;
    };
    top_slow: Array<{
      action: string; count: number;
      p50_ms: number | null; p95_ms: number | null; max_ms: number;
    }>;
  } | null> {
    if (!isDbMode) return null;
    try {
      return await safeFetch(`${API_BASE}?action=server_stats_history&range=${range}`);
    } catch { return null; }
  },

  async getSecuritySignals(window: '15m' | '1h' | '24h' | '7d' = '1h'): Promise<{
    window: string;
    window_minutes: number;
    threat_level: 'high' | 'medium' | 'low';
    summary: {
      brute_force_ips: number;
      scanning_ips: number;
      flood_ips: number;
      hijacking_suspects: number;
      suspicious_agents: number;
      exfiltration_suspects: number;
    };
    thresholds: {
      brute_force_min_failed: number;
      scanning_min_4xx: number;
      flood_min_requests_per_h: number;
      exfiltration_min_actions: number;
      suspicious_ua_regex: string;
    };
    brute_force: Array<{ ip_address: string; attempts: number; failed: number; success: number; first_seen: string; last_seen: string }>;
    scanning: Array<{ ip_address: string; c4xx: number; distinct_actions: number; last_seen: string }>;
    flood: Array<{ ip_address: string; total: number; c4xx: number; c5xx: number; avg_ms: number; last_seen: string }>;
    top_ips: Array<{ ip_address: string; total: number; c4xx: number; c5xx: number; avg_ms: number; last_seen: string }>;
    hijacking: Array<{ user_id: string; user_name: string; user_email: string; company_name: string; ip_count: number; ips: string; last_seen: string }>;
    suspicious_agents: Array<{ user_id: string; user_name: string; ip_address: string; user_agent: string; last_seen: string }>;
    exfiltration: Array<{ user_id: string; user_name: string; action: string; cnt: number; last_ip: string; first_seen: string; last_seen: string }>;
  } | null> {
    if (!isDbMode) return null;
    try {
      return await safeFetch(`${API_BASE}?action=security_signals&window=${window}`);
    } catch { return null; }
  },

  // ===== AUDIT LOG =====

  async getAuditLogs(params: { page?: number; limit?: number; filterAction?: string; filterUser?: string } = {}): Promise<{ logs: Array<{ id: number; user_id: string; user_name: string; user_role: string; action: string; target_type: string; target_id: string; details: string; ip_address: string; created_at: string }>; total: number; page: number; pages: number }> {
    if (isDbMode) {
      const q = new URLSearchParams();
      q.set('action', 'get_audit_logs');
      if (params.page) q.set('page', String(params.page));
      if (params.limit) q.set('limit', String(params.limit));
      if (params.filterAction) q.set('filter_action', params.filterAction);
      if (params.filterUser) q.set('filter_user', params.filterUser);
      const data = await safeFetch(`${API_BASE}?${q.toString()}`);
      return data || { logs: [], total: 0, page: 1, pages: 0 };
    }
    return { logs: [], total: 0, page: 1, pages: 0 };
  },

  // ===== EMAIL TEMPLATES =====

  async getEmailTemplates(): Promise<Array<{ id: string; name: string; subject: string; body: string; created_at: string }>> {
    if (isDbMode) {
      const data = await safeFetch(`${API_BASE}?action=get_email_templates`);
      return Array.isArray(data) ? data : [];
    }
    return [];
  },

  async saveEmailTemplate(tpl: { id?: string; name: string; subject: string; body: string }): Promise<{ id: string }> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=save_email_template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tpl)
      });
      if (!result || result.error) throw new Error(result?.error || 'Erreur sauvegarde template');
      return result;
    }
    return { id: 'local_' + Date.now() };
  },

  async getSmtpSettings(): Promise<{ smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; from_name: string; from_email: string; has_password: boolean } | null> {
    if (!isDbMode) return null;
    try {
      return await safeFetch(`${API_BASE}?action=get_smtp_settings`);
    } catch { return null; }
  },

  async saveSmtpSettings(settings: { smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; smtp_pass?: string; from_name: string; from_email: string }): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=save_smtp_settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
      timeout: 15000
    });
    if (!result?.success) throw new Error(result?.error || 'Erreur sauvegarde SMTP');
  },

  async getGlobalSmtp(): Promise<{ smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; from_name: string; from_email: string; has_password: boolean } | null> {
    try {
      return await safeFetch(`${API_BASE}?action=get_global_smtp`);
    } catch { return null; }
  },

  async saveGlobalSmtp(settings: { smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; smtp_pass?: string; from_name: string; from_email: string }): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=save_global_smtp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
      timeout: 15000
    });
    if (!result?.success) throw new Error(result?.error || 'Erreur sauvegarde SMTP global');
  },

  async testSmtp(): Promise<{ success: boolean; message?: string; error?: string }> {
    const result = await safeFetch(`${API_BASE}?action=test_smtp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      timeout: 30000
    });
    return result || { success: false, error: 'Erreur reseau' };
  },

  async deleteEmailTemplate(id: string): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=delete_email_template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (!result || result.error) throw new Error(result?.error || 'Erreur suppression template');
    }
  },

  // ===== LEAD IMPORT TEMPLATES =====
  // Mapping CSV header -> Orion field, réutilisable d'un import à l'autre.

  async getLeadImportTemplates(): Promise<Array<{ id: string; name: string; config: Record<string, string>; created_at?: string; updated_at?: string }>> {
    if (!isDbMode) return [];
    const data = await safeFetch(`${API_BASE}?action=get_lead_import_templates`);
    if (!Array.isArray(data)) return [];
    return data.map((t: any) => {
      let cfg: Record<string, string> = {};
      try { cfg = typeof t.config === 'string' ? JSON.parse(t.config) : (t.config || {}); } catch { cfg = {}; }
      return {
        id: String(t.id),
        name: String(t.name || ''),
        config: cfg,
        created_at: t.created_at,
        updated_at: t.updated_at,
      };
    });
  },

  async saveLeadImportTemplate(tpl: { id?: string; name: string; config: Record<string, string> }): Promise<{ id: string }> {
    if (!isDbMode) return { id: 'local_' + Date.now() };
    const result = await safeFetch(`${API_BASE}?action=save_lead_import_template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tpl)
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur sauvegarde modèle');
    return result;
  },

  async deleteLeadImportTemplate(id: string): Promise<void> {
    if (!isDbMode) return;
    const result = await safeFetch(`${API_BASE}?action=delete_lead_import_template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur suppression modèle');
  },

  // ===== RÈGLES D'AUTO-AFFECTATION =====
  // Le helper PHP `autoAssignLead` lit ces règles au moment de `create_lead`.
  // L'UI envoie la liste complète ordonnée via `saveAssignmentRules`.

  async getAssignmentRules(): Promise<AssignmentRule[]> {
    if (!isDbMode) return [];
    const data = await safeFetch(`${API_BASE}?action=get_assignment_rules`);
    if (!Array.isArray(data)) return [];
    return data.map((r: any) => {
      let conditions: AssignmentRule['conditions'] = {};
      try { conditions = typeof r.conditions === 'string' ? JSON.parse(r.conditions) : (r.conditions || {}); } catch { conditions = {}; }
      let pool: string[] | undefined;
      if (r.action_pool) {
        try { pool = typeof r.action_pool === 'string' ? JSON.parse(r.action_pool) : r.action_pool; } catch { pool = []; }
      }
      return {
        id: String(r.id),
        position: Number(r.position ?? 0),
        name: String(r.name ?? ''),
        enabled: !!Number(r.enabled),
        conditions,
        actionType: (r.action_type === 'round_robin' ? 'round_robin' : 'user') as AssignmentRule['actionType'],
        actionUserId: r.action_user_id || null,
        actionPool: pool,
        lastAssignedUserId: r.last_assigned_user_id || null,
      } as AssignmentRule;
    });
  },

  async saveAssignmentRules(rules: AssignmentRule[]): Promise<{ count: number }> {
    if (!isDbMode) return { count: 0 };
    const payload = {
      rules: rules.map(r => ({
        id: r.id,
        name: r.name,
        enabled: r.enabled,
        conditions: r.conditions,
        actionType: r.actionType,
        actionUserId: r.actionType === 'user' ? r.actionUserId : null,
        actionPool: r.actionType === 'round_robin' ? (r.actionPool || []) : null,
      })),
    };
    const result = await safeFetch(`${API_BASE}?action=save_assignment_rules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur sauvegarde règles');
    return { count: Number(result.count ?? 0) };
  },

  async deleteAssignmentRule(id: string): Promise<void> {
    if (!isDbMode) return;
    const result = await safeFetch(`${API_BASE}?action=delete_assignment_rule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur suppression règle');
  },

  // ===== PROFILS DE RÔLES PERSONNALISÉS =====

  async getCustomRoles(): Promise<Array<{ id: string; name: string; description: string; permissions: Record<string, boolean>; leadSheetTemplateId?: string | null }>> {
    if (!isDbMode) return [];
    const data = await safeFetch(`${API_BASE}?action=get_custom_roles`);
    if (!Array.isArray(data)) return [];
    return data.map((r: any) => {
      let perms: Record<string, boolean> = {};
      try { perms = typeof r.permissions === 'string' ? JSON.parse(r.permissions) : (r.permissions || {}); } catch { perms = {}; }
      return {
        id: String(r.id),
        name: String(r.name || ''),
        description: String(r.description || ''),
        permissions: perms,
        leadSheetTemplateId: r.default_lead_sheet_template_id ?? r.leadSheetTemplateId ?? null,
      };
    });
  },

  async saveCustomRole(role: { id?: string; name: string; description?: string; permissions: Record<string, boolean>; leadSheetTemplateId?: string | null }): Promise<{ id: string }> {
    if (!isDbMode) return { id: 'local_' + Date.now() };
    const result = await safeFetch(`${API_BASE}?action=save_custom_role`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(role),
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur sauvegarde profil');
    return result;
  },

  async deleteCustomRole(id: string): Promise<void> {
    if (!isDbMode) return;
    const result = await safeFetch(`${API_BASE}?action=delete_custom_role`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur suppression profil');
  },

  // ===== CALL LOGGING =====

  async logCall(payload: { leadId?: string; duration: number; direction: 'inbound' | 'outbound'; notes?: string }): Promise<string> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=log_call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: payload.leadId, duration: payload.duration, direction: payload.direction, notes: payload.notes || '' })
      });
      if (result?.success) return result.id;
      throw new Error(result?.error || 'Echec enregistrement appel');
    }
    // Fallback local
    const callId = `call_${Date.now()}`;
    return callId;
  },

  async getCallLogs(filters: { leadId?: string; userId?: string; limit?: number }): Promise<any[]> {
    if (isDbMode) {
      const params: string[] = [];
      if (filters.leadId) params.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters.userId) params.push(`user_id=${encodeURIComponent(filters.userId)}`);
      if (filters.limit) params.push(`limit=${filters.limit}`);
      const data = await safeFetch(`${API_BASE}?action=get_call_logs&${params.join('&')}`);
      return Array.isArray(data) ? data : [];
    }
    return [];
  },

  async getCallStats(companyId: string): Promise<CallStat[]> {
    if (isDbMode) {
      const data = await safeFetch(`${API_BASE}?action=get_call_stats&company_id=${encodeURIComponent(companyId)}`);
      if (Array.isArray(data)) {
        return data.map((d: any) => ({
          userId: d.user_id,
          callCount: Number(d.call_count),
          avgDuration: Math.round(Number(d.avg_duration)),
          totalDuration: Number(d.total_duration),
          period: 'month' as const
        }));
      }
    }
    return MOCK_CALL_STATS;
  },

  // ===== OPPORTUNITES =====

  async getOpportunities(filters: { companyId: string, leadId?: string, clientCompanyName?: string }): Promise<Opportunity[]> {
    if (isDbMode) {
      const query: string[] = [`company_id=${encodeURIComponent(filters.companyId)}`];
      if (filters.leadId) query.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters.clientCompanyName) query.push(`client_company_name=${encodeURIComponent(filters.clientCompanyName)}`);
      const apiData = await safeFetch(`${API_BASE}?action=get_opportunities&${query.join('&')}`);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapOpportunityFromApi);
        sessionOpportunities = [
          ...sessionOpportunities.filter(o => String(o.companyId) !== String(filters.companyId)),
          ...mapped
        ];
        saveToStorage();
        return mapped;
      }
      throw new Error('Impossible de recuperer les opportunites depuis la base');
    }

    return sessionOpportunities.filter(o => {
      if (String(o.companyId) !== String(filters.companyId)) return false;
      if (filters.leadId && String(o.leadId || '') !== String(filters.leadId)) return false;
      if (filters.clientCompanyName && String(o.clientCompanyName || '') !== String(filters.clientCompanyName)) return false;
      return true;
    });
  },

  /** Variante paginée de getOpportunities. */
  async getOpportunitiesPaginated(filters: { companyId: string; leadId?: string; clientCompanyName?: string; page?: number; limit?: number }): Promise<PaginatedResult<Opportunity>> {
    const page  = filters.page  ?? 1;
    const limit = filters.limit ?? 50;

    if (!isDbMode) {
      const filtered = sessionOpportunities.filter(o => {
        if (String(o.companyId) !== String(filters.companyId)) return false;
        if (filters.leadId && String(o.leadId || '') !== String(filters.leadId)) return false;
        if (filters.clientCompanyName && String(o.clientCompanyName || '') !== String(filters.clientCompanyName)) return false;
        return true;
      });
      const start = (page - 1) * limit;
      return { items: filtered.slice(start, start + limit), total: filtered.length, page, limit };
    }

    const query: string[] = [
      `company_id=${encodeURIComponent(filters.companyId)}`,
      `page=${page}`,
      `limit=${limit}`,
    ];
    if (filters.leadId) query.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
    if (filters.clientCompanyName) query.push(`client_company_name=${encodeURIComponent(filters.clientCompanyName)}`);
    const apiData = await safeFetch(`${API_BASE}?action=get_opportunities&${query.join('&')}`);
    if (!apiData || !Array.isArray(apiData.items)) {
      throw new Error('Réponse paginée inattendue pour get_opportunities');
    }
    return {
      items: apiData.items.map(mapOpportunityFromApi),
      total: Number(apiData.total) || 0,
      page: Number(apiData.page) || page,
      limit: Number(apiData.limit) || limit,
    };
  },

  /**
   * Analytics — récupère les données brutes consolidées pour la page Performance.
   * Le backend applique la portée par rôle (USER = soi, MANAGER = équipe, ADMIN = tout).
   * Le filtre `agentId` est optionnel ; il est ignoré si l'agent demandé est hors du
   * scope du rôle (ex: un manager qui demande un agent qui n'est pas dans son équipe).
   *
   * La période [from, to] est étendue côté serveur à la période précédente (même
   * durée juste avant) pour permettre les calculs de delta côté client sans
   * deuxième round-trip.
   */
  async getAnalyticsData(params: { from: string; to: string; agentId?: string }): Promise<{
    period: { from: string; to: string; previousFrom: string; durationDays: number };
    scope: { role: string; isAdminLike: boolean; isManager: boolean; isUser: boolean; requestedAgent: string };
    agents: Array<{ id: string; name: string; role: string; managerId: string | null }>;
    leads: Array<{
      id: string; assignedTo: string | null; status: string; sectorActivity: string | null;
      tag: string | null; potentialRevenue: string | null; source: string | null;
      clientCompanyName: string | null; name: string; createdAt: string; isFavorite: boolean;
    }>;
    leadActivities: Array<{ id: string; leadId: string; userId: string; type: string; createdAt: string }>;
    tasks: Array<{
      id: string; createdBy: string; leadId: string | null; clientCompanyName: string | null;
      title: string; status: 'todo' | 'done'; dueAt: string; completedAt: string | null; createdAt: string;
    }>;
    opportunities: Array<{
      id: string; leadId: string | null; clientCompanyName: string; subject: string;
      totalAmount: number; createdBy: string; createdAt: string;
    }>;
    callLogs: Array<{
      id: string; userId: string; leadId: string | null; callDate: string;
      duration: number; direction: 'inbound' | 'outbound';
    }>;
    appointments: Array<{
      id: string; leadId: string; userId: string; type: 'PHONE' | 'VIDEO' | 'PHYSICAL';
      startTime: string; endTime: string; createdAt: string;
    }>;
  }> {
    const query: string[] = [
      `from=${encodeURIComponent(params.from)}`,
      `to=${encodeURIComponent(params.to)}`,
    ];
    if (params.agentId && params.agentId !== 'all') {
      query.push(`agent_id=${encodeURIComponent(params.agentId)}`);
    }
    const data = await safeFetch(`${API_BASE}?action=get_analytics_data&${query.join('&')}`);
    if (!data || typeof data !== 'object') {
      throw new Error('Réponse invalide pour get_analytics_data');
    }
    return data;
  },

  async createOpportunity(
    payload: {
      companyId: string;
      leadId?: string;
      clientCompanyName: string;
      subject: string;
      comment?: string;
      createdBy: string;
      items: Array<{ label: string; quantity: number; unitPrice: number }>;
    },
    currentUser?: { id: string; name: string }
  ): Promise<Opportunity> {
    const now = new Date().toISOString();
    const id = 'opp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
    const items: OpportunityItem[] = payload.items.map((item, idx) => {
      const totalPrice = Number((item.quantity * item.unitPrice).toFixed(2));
      return {
        id: `oppi_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 4)}`,
        opportunityId: id,
        label: item.label,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        totalPrice
      };
    });
    const totalAmount = Number(items.reduce((sum, item) => sum + item.totalPrice, 0).toFixed(2));

    const newOpportunity: Opportunity = {
      id,
      companyId: payload.companyId,
      leadId: payload.leadId,
      clientCompanyName: payload.clientCompanyName,
      subject: payload.subject,
      comment: payload.comment || '',
      totalAmount,
      createdBy: payload.createdBy,
      createdAt: now,
      items
    };

    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=create_opportunity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newOpportunity.id,
          companyId: newOpportunity.companyId,
          leadId: newOpportunity.leadId,
          clientCompanyName: newOpportunity.clientCompanyName,
          subject: newOpportunity.subject,
          comment: newOpportunity.comment,
          totalAmount: newOpportunity.totalAmount,
          createdBy: newOpportunity.createdBy,
          items: newOpportunity.items.map(i => ({
            id: i.id,
            label: i.label,
            quantity: i.quantity,
            unitPrice: i.unitPrice
          }))
        })
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation de l opportunite echouee cote base');
      }
      const persisted = mapOpportunityFromApi(apiResult);
      sessionOpportunities = [persisted, ...sessionOpportunities];
      saveToStorage();

      if (currentUser && persisted.leadId) {
        createActivity(
          persisted.leadId,
          currentUser.id,
          currentUser.name,
          LeadActivityType.INFO_UPDATED,
          `Nouvelle opportunite creee : ${persisted.subject}`,
          { opportunityId: persisted.id, totalAmount: persisted.totalAmount }
        );
      }
      return persisted;
    }

    sessionOpportunities = [newOpportunity, ...sessionOpportunities];
    saveToStorage();

    if (currentUser && newOpportunity.leadId) {
      createActivity(
        newOpportunity.leadId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.INFO_UPDATED,
        `Nouvelle opportunite creee : ${newOpportunity.subject}`,
        { opportunityId: newOpportunity.id, totalAmount: newOpportunity.totalAmount }
      );
    }
    return newOpportunity;
  },

  async updateOpportunity(opportunityId: string, updates: { subject: string, comment?: string, clientCompanyName?: string, items: OpportunityItem[] }): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=update_opportunity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opportunityId, ...updates })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Mise a jour de l\'opportunite echouee');
      }
    }
    const totalAmount = updates.items.reduce((sum, it) => sum + (it.quantity * it.unitPrice), 0);
    const index = sessionOpportunities.findIndex(o => o.id === opportunityId);
    if (index !== -1) {
      sessionOpportunities[index] = {
        ...sessionOpportunities[index],
        subject: updates.subject,
        comment: updates.comment || '',
        clientCompanyName: updates.clientCompanyName || sessionOpportunities[index].clientCompanyName,
        items: updates.items,
        totalAmount
      };
      saveToStorage();
    }
  },

  async deleteOpportunity(opportunityId: string): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=delete_opportunity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opportunityId })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Suppression de l\'opportunite echouee');
      }
    }
    sessionOpportunities = sessionOpportunities.filter(o => o.id !== opportunityId);
    saveToStorage();
  },

  // ===== RENDEZ-VOUS =====

  async getAppointments(filters: { leadId?: string, userId?: string }): Promise<Appointment[]> {
    if (isDbMode) {
      const q: string[] = [];
      if (filters?.leadId) q.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters?.userId) q.push(`user_id=${encodeURIComponent(filters.userId)}`);
      const url = `${API_BASE}?action=get_appointments${q.length ? `&${q.join('&')}` : ''}`;
      const apiData = await safeFetch(url);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapAppointmentFromApi);
        if (filters?.leadId) {
          sessionAppointments = [
            ...sessionAppointments.filter(a => String(a.leadId) !== String(filters.leadId)),
            ...mapped
          ];
        } else {
          sessionAppointments = mapped;
        }
        return mapped;
      }
      throw new Error('Impossible de recuperer les rendez-vous depuis la base');
    }

    let filtered = [...sessionAppointments];
    if (filters) {
      if (filters.leadId) filtered = filtered.filter(a => String(a.leadId) === String(filters.leadId));
      if (filters.userId) filtered = filtered.filter(a => String(a.userId) === String(filters.userId));
    }
    return filtered;
  },

  async addAppointment(appt: Omit<Appointment, 'id'>, currentUser?: { id: string, name: string }): Promise<Appointment> {
    const newAppt = { 
      ...appt, 
      id: 'appt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5) 
    };
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=add_appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAppt)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation du rendez-vous echouee cote base');
      }
      const persisted = mapAppointmentFromApi(apiResult);
      sessionAppointments.push(persisted);

      if (currentUser && appt.leadId) {
        const appointmentDate = new Date(appt.start).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'long',
          hour: '2-digit',
          minute: '2-digit'
        });
        createActivity(
          appt.leadId,
          currentUser.id,
          currentUser.name,
          LeadActivityType.APPOINTMENT_CREATED,
          `Rendez-vous planifie : ${appt.title} le ${appointmentDate}`,
          { appointmentId: persisted.id, appointmentType: appt.type }
        );
      }

      saveToStorage();
      return persisted;
    }

    sessionAppointments.push(newAppt);
    
    // ACTIVITÉ
    if (currentUser && appt.leadId) {
      const appointmentDate = new Date(appt.start).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
      createActivity(
        appt.leadId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.APPOINTMENT_CREATED,
        `Rendez-vous planifié : ${appt.title} le ${appointmentDate}`,
        { appointmentId: newAppt.id, appointmentType: appt.type }
      );

      // NOTIFICATION : Rendez-vous créé
      const lead = sessionLeads.find(l => l.id === appt.leadId);
      if (lead && lead.assignedTo && lead.assignedTo !== currentUser.id) {
        await api.createNotification({
          userId: lead.assignedTo,
          type: NotificationType.APPOINTMENT_CREATED,
          title: 'Nouveau rendez-vous',
          message: `${currentUser.name} a planifié un rendez-vous : ${appt.title}`,
          leadId: appt.leadId,
          leadName: lead.name,
          isRead: false,
          metadata: { appointmentId: newAppt.id }
        });
      }
    }
    
    saveToStorage();
    return newAppt;
  },
  
  async updateAppointment(id: string, updates: Partial<Appointment>): Promise<Appointment | null> {
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=update_appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...updates })
      });
      if (!apiResult || apiResult.error) return null;
    }
    const idx = sessionAppointments.findIndex(a => a.id === id);
    if (idx === -1) return null;
    sessionAppointments[idx] = { ...sessionAppointments[idx], ...updates };
    saveToStorage();
    return sessionAppointments[idx];
  },
  
  async deleteAppointment(id: string): Promise<void> {
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=delete_appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (!apiResult || apiResult.error) {
        throw new Error('Suppression du rendez-vous echouee cote base');
      }
    }
    sessionAppointments = sessionAppointments.filter(a => a.id !== id);
    saveToStorage();
  },

  // ===== COMMENTAIRES =====

  async getComments(leadId: string): Promise<LeadComment[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_comments&lead_id=${encodeURIComponent(leadId)}`);
      if (Array.isArray(apiData)) {
        localComments = localComments
          .filter(c => String(c.leadId) !== String(leadId))
          .concat(apiData.map(mapCommentFromApi));
        return apiData.map(mapCommentFromApi);
      }
      throw new Error('Impossible de recuperer les commentaires depuis la base');
    }
    return localComments.filter(c => String(c.leadId) === String(leadId));
  },

  async addComment(comment: Omit<LeadComment, 'id' | 'createdAt'>, currentUser?: { id: string, name: string }): Promise<LeadComment> {
    const newComment: LeadComment = { 
      ...comment, 
      id: 'lc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5), 
      createdAt: new Date().toISOString() 
    };
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=add_comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(comment)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation du commentaire echouee cote base');
      }
      const persisted = mapCommentFromApi(apiResult);
      localComments.push(persisted);

      if (currentUser) {
        createActivity(
          comment.leadId,
          currentUser.id,
          currentUser.name,
          LeadActivityType.COMMENT_ADDED,
          `Commentaire ajoute : "${comment.content.substring(0, 50)}${comment.content.length > 50 ? '...' : ''}"`
        );
      }
      return persisted;
    }

    localComments.push(newComment);
    
    // ACTIVITÉ
    if (currentUser) {
      createActivity(
        comment.leadId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.COMMENT_ADDED,
        `Commentaire ajouté : "${comment.content.substring(0, 50)}${comment.content.length > 50 ? '...' : ''}"`
      );

      // NOTIFICATIONS
      // 1. Détecter les mentions (@utilisateur)
      const mentions = comment.content.match(/@(\w+)/g);
      if (mentions) {
        const mentionedUsers = sessionUsers.filter(u => 
          mentions.some(m => u.name.toLowerCase().includes(m.slice(1).toLowerCase()))
        );
        
        for (const user of mentionedUsers) {
          if (user.id !== currentUser.id) {
            await api.createNotification({
              userId: user.id,
              type: NotificationType.MENTION,
              title: 'Vous avez été mentionné',
              message: `${currentUser.name} vous a mentionné dans un commentaire`,
              leadId: comment.leadId,
              isRead: false
            });
          }
        }
      }

      // 2. Notifier l'assigné du prospect
      const lead = sessionLeads.find(l => l.id === comment.leadId);
      if (lead && lead.assignedTo && lead.assignedTo !== currentUser.id) {
        await api.createNotification({
          userId: lead.assignedTo,
          type: NotificationType.NEW_COMMENT,
          title: 'Nouveau commentaire',
          message: `${currentUser.name} a ajouté un commentaire sur ${lead.name}`,
          leadId: comment.leadId,
          leadName: lead.name,
          isRead: false
        });
      }
    }
    
    return newComment;
  },

  async deleteLeadComment(commentId: string): Promise<void> {
    if (!isDbMode) {
      // Mode démo : retire localement.
      const idx = localComments.findIndex(c => c.id === commentId);
      if (idx >= 0) localComments.splice(idx, 1);
      return;
    }
    const result = await safeFetch(`${API_BASE}?action=delete_lead_comment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: commentId }),
    });
    if (!result || result.error) throw new Error(result?.error || 'Erreur suppression commentaire');
  },

  // ===== TIMELINE (ACTIVITÉS) =====

  async getLeadActivities(leadId: string): Promise<LeadActivity[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_lead_activities&lead_id=${encodeURIComponent(leadId)}`);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapActivityFromApi);
        sessionActivities = [
          ...sessionActivities.filter(a => String(a.leadId) !== String(leadId)),
          ...mapped
        ];
        return mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }
      throw new Error('Impossible de recuperer la timeline depuis la base');
    }
    const activities = sessionActivities.filter(a => String(a.leadId) === String(leadId));
    return activities.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  async addLeadActivity(activity: Omit<LeadActivity, 'id' | 'createdAt'>): Promise<LeadActivity> {
    if (isDbMode) {
      const payload = {
        ...activity,
        id: 'activity_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
      };
      const apiResult = await safeFetch(`${API_BASE}?action=add_lead_activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation de timeline echouee cote base');
      }
      const persisted = mapActivityFromApi(apiResult);
      sessionActivities.push(persisted);
      saveToStorage();
      return persisted;
    }
    return createActivity(
      activity.leadId,
      activity.userId,
      activity.userName,
      activity.type,
      activity.description,
      activity.metadata
    );
  },

  // ===== NOTIFICATIONS =====

  async getNotifications(userId: string): Promise<Notification[]> {
    if (isDbMode) {
      try {
        const apiData = await safeFetch(`${API_BASE}?action=get_notifications&user_id=${encodeURIComponent(userId)}`);
        if (Array.isArray(apiData)) {
          const mapped: Notification[] = apiData.map((n: any) => ({
            id: n.id,
            userId: n.user_id,
            type: n.type as NotificationType,
            title: n.title,
            message: n.message,
            leadId: n.lead_id || undefined,
            leadName: n.lead_name || undefined,
            isRead: !!n.is_read,
            createdAt: n.created_at,
            metadata: n.metadata || undefined,
          }));
          sessionNotifications = [
            ...mapped,
            ...sessionNotifications.filter(sn => sn.userId !== userId)
          ];
          return mapped;
        }
      } catch (e) {
        console.warn('get_notifications API failed, fallback local', e);
      }
    }
    const userNotifications = sessionNotifications
      .filter(n => n.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return userNotifications;
  },

  async createNotification(notification: Omit<Notification, 'id' | 'createdAt'>): Promise<Notification> {
    const newNotification: Notification = {
      ...notification,
      id: 'notif_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      createdAt: new Date().toISOString()
    };

    sessionNotifications.push(newNotification);
    saveToStorage();

    if (isDbMode) {
      safeFetch(`${API_BASE}?action=create_notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newNotification.id,
          userId: notification.userId,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          leadId: notification.leadId || null,
          leadName: notification.leadName || null,
          metadata: notification.metadata || null,
        })
      }).catch(err => console.warn('create_notification sync failed:', err.message));
    }

    return newNotification;
  },

  async markNotificationAsRead(notificationId: string): Promise<void> {
    const index = sessionNotifications.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      sessionNotifications[index].isRead = true;
      saveToStorage();
    }
    if (isDbMode) {
      safeFetch(`${API_BASE}?action=mark_notification_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: notificationId })
      }).catch(err => console.warn('mark_notification_read sync failed:', err.message));
    }
  },

  async markAllNotificationsAsRead(userId: string): Promise<void> {
    sessionNotifications = sessionNotifications.map(n =>
      n.userId === userId ? { ...n, isRead: true } : n
    );
    saveToStorage();
    if (isDbMode) {
      safeFetch(`${API_BASE}?action=mark_all_notifications_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      }).catch(err => console.warn('mark_all_notifications_read sync failed:', err.message));
    }
  },

  async deleteNotification(notificationId: string): Promise<void> {
    sessionNotifications = sessionNotifications.filter(n => n.id !== notificationId);
    saveToStorage();
    if (isDbMode) {
      safeFetch(`${API_BASE}?action=delete_notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: notificationId })
      }).catch(err => console.warn('delete_notification sync failed:', err.message));
    }
  },

  async getUnreadNotificationsCount(userId: string): Promise<number> {
    return sessionNotifications.filter(n => n.userId === userId && !n.isRead).length;
  },

  // Vérifier les prospects inactifs (à appeler périodiquement)
  async checkInactiveLeads(): Promise<void> {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const inactiveLeads = sessionLeads.filter(lead => {
      const lastActivity = sessionActivities
        .filter(a => a.leadId === lead.id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      
      if (!lastActivity) return false;
      
      const activityDate = new Date(lastActivity.createdAt);
      return activityDate < sevenDaysAgo && lead.status !== 'won' && lead.status !== 'lost';
    });
    
    for (const lead of inactiveLeads) {
      // Vérifier si on n'a pas déjà envoyé une notification récente
      const existingNotif = sessionNotifications.find(n => 
        n.leadId === lead.id && 
        n.type === NotificationType.LEAD_INACTIVE &&
        new Date(n.createdAt) > sevenDaysAgo
      );
      
      if (!existingNotif && lead.assignedTo) {
        await api.createNotification({
          userId: lead.assignedTo,
          type: NotificationType.LEAD_INACTIVE,
          title: 'Prospect inactif',
          message: `Aucune activité sur ${lead.name} depuis plus de 7 jours`,
          leadId: lead.id,
          leadName: lead.name,
          isRead: false
        });
      }
    }
  },

  // ============================================================================
  // Chat interne multi-tenant (Sprint 1 MVP)
  // ============================================================================

  async chatListConversations(): Promise<ChatConversation[]> {
    const data = await safeFetch(`${API_BASE}?action=chat_list_conversations`);
    if (!data || data.error) throw new Error(data?.error || 'Erreur récupération conversations');
    return data.conversations || [];
  },

  /** Crée (ou retrouve) une conv directe 1-to-1 avec un autre user du tenant. Idempotent. */
  async chatCreateDirect(targetUserId: string): Promise<{ id: string; created: boolean }> {
    const data = await safeFetch(`${API_BASE}?action=chat_create_direct`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_user_id: targetUserId }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur création conversation');
    return { id: data.id, created: !!data.created };
  },

  /** Pagination cursor-based des messages d'une conv (les plus récents d'abord). */
  async chatListMessages(conversationId: string, cursor?: string, limit = 50): Promise<{ messages: ChatMessage[]; nextCursor: string | null; hasMore: boolean }> {
    const params = new URLSearchParams({ action: 'chat_list_messages', conversation_id: conversationId, limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    const data = await safeFetch(`${API_BASE}?${params}`);
    if (!data || data.error) throw new Error(data?.error || 'Erreur récupération messages');
    return {
      messages: data.messages || [],
      nextCursor: data.next_cursor || null,
      hasMore: !!data.has_more,
    };
  },

  async chatSendMessage(conversationId: string, content: string): Promise<ChatMessage> {
    const data = await safeFetch(`${API_BASE}?action=chat_send_message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId, content }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur envoi message');
    return data;
  },

  async chatMarkRead(conversationId: string): Promise<void> {
    const data = await safeFetch(`${API_BASE}?action=chat_mark_read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId }),
    });
    if (data && data.error) throw new Error(data.error);
  },

  async chatUnreadCount(): Promise<number> {
    try {
      const data = await safeFetch(`${API_BASE}?action=chat_unread_count`);
      return data?.count ?? 0;
    } catch {
      return 0;
    }
  },

  // ===== Sprint 2 : groupes + édition/suppression + recherche =====

  async chatCreateGroup(name: string, participantIds: string[], isPrivate: boolean = false): Promise<{ id: string }> {
    const data = await safeFetch(`${API_BASE}?action=chat_create_group`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        participant_ids: participantIds,
        is_private: isPrivate ? '1' : '0',
      }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur création groupe');
    return { id: data.id };
  },

  async chatAddParticipant(conversationId: string, userId: string): Promise<void> {
    const data = await safeFetch(`${API_BASE}?action=chat_add_participant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId, user_id: userId }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur ajout participant');
  },

  /** Quitte une conversation. Si plus aucun membre ne reste, la conv est supprimée côté serveur. */
  async chatLeaveConversation(conversationId: string): Promise<{ conversationDeleted: boolean }> {
    const data = await safeFetch(`${API_BASE}?action=chat_leave_conversation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur');
    return { conversationDeleted: !!data.conversation_deleted };
  },

  /** Édite un message dans la fenêtre des 15 min après envoi. Auteur uniquement. */
  async chatEditMessage(messageId: string, content: string): Promise<{ editedAt: string }> {
    const data = await safeFetch(`${API_BASE}?action=chat_edit_message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message_id: messageId, content }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur édition');
    return { editedAt: data.edited_at };
  },

  /** Soft delete d'un message. Auteur ou COMPANY_ADMIN. Idempotent. */
  async chatDeleteMessage(messageId: string): Promise<void> {
    const data = await safeFetch(`${API_BASE}?action=chat_delete_message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message_id: messageId }),
    });
    if (!data || data.error) throw new Error(data?.error || 'Erreur suppression');
  },

  /** Recherche FULLTEXT dans les conversations où l'utilisateur participe. */
  async chatSearch(query: string, limit: number = 20): Promise<ChatSearchResult[]> {
    const params = new URLSearchParams({ action: 'chat_search', q: query, limit: String(limit) });
    const data = await safeFetch(`${API_BASE}?${params}`);
    if (!data || data.error) throw new Error(data?.error || 'Erreur recherche');
    return data.results || [];
  },
};

// Export CSV amélioré
export const exportToCSV = (data: any[], filename: string) => {
  if (!data || !data.length) {
    alert('⚠️ Aucune donnée à exporter');
    return;
  }
  
  try {
    const separator = ',';
    const keys = Object.keys(data[0]);
    const csvContent =
      keys.join(separator) +
      '\n' +
      data.map(row => {
        return keys.map(k => {
          let cell = row[k] === null || row[k] === undefined ? '' : row[k];
          cell = cell instanceof Date ? cell.toLocaleString() : cell.toString().replace(/"/g, '""');
          if (cell.search(/("|,|\n)/g) >= 0) cell = `"${cell}"`;
          return cell;
        }).join(separator);
      }).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log(`✓ Export CSV réussi: ${filename}`);
    }
  } catch (error) {
    console.error('Erreur export CSV:', error);
    alert('⚠️ Erreur lors de l\'export CSV');
  }
};

